import type { Evento, TipoDocumento } from '@/lib/supabase/tipos-bd'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Cargando } from '@/shared/ui/Estados/Cargando'
import { SinDatos } from '@/shared/ui/Estados/SinDatos'
import { fechaHora } from '@/shared/utilidades/formato'

import { NOMBRE_ROL } from '@/shared/dominio/roles'

import { useBitacora } from './useBitacora'
import estilos from './Bitacora.module.css'

/** Cómo se titula cada tipo de evento en la historia. */
const TITULO_EVENTO = {
  creacion: 'Se creó el documento',
  cambio_estado: 'Cambió de estado',
  aprobacion: 'Aprobado',
  rechazo: 'Rechazado',
  asignacion: 'Se asignó responsable',
  nota: 'Nota',
} as const

/** Nombres legibles de los estados, para no mostrar 'pendiente_cotizar'. */
function nombreEstado(estado: string | null): string | null {
  if (!estado) return null
  return estado.replace(/_/g, ' ')
}

function claseDelPunto(tipo: Evento['tipo']): string {
  if (tipo === 'aprobacion') return estilos.puntoAprobacion
  if (tipo === 'rechazo') return estilos.puntoRechazo
  if (tipo === 'creacion') return estilos.puntoCreacion
  return ''
}

type PropsBitacora = {
  tipo: TipoDocumento
  documentoId: string
}

/**
 * La historia completa de un documento.
 *
 * Sirve para los tres documentos de la cadena, porque la bitácora es una sola:
 * quién hizo qué, cuándo y con qué justificación. Es el respaldo de las dos
 * compuertas del recorrido.
 */
export function Bitacora({ tipo, documentoId }: PropsBitacora) {
  const { eventos, cargando, error } = useBitacora(tipo, documentoId)

  if (cargando) return <Cargando compacto mensaje="CONSULTANDO HISTORIAL TÉCNICO..." />

  if (error) {
    return (
      <Aviso tipo="error" titulo="No se pudo cargar la historia">
        {error}
      </Aviso>
    )
  }

  if (eventos.length === 0) {
    return <SinDatos titulo="Sin movimientos registrados" />
  }

  return (
    <div className={estilos.linea}>
      {eventos.map((evento) => {
        const desde = nombreEstado(evento.estado_anterior)
        const hasta = nombreEstado(evento.estado_nuevo)

        return (
          <article key={evento.id} className={estilos.evento}>
            <span className={`${estilos.punto} ${claseDelPunto(evento.tipo)}`} aria-hidden="true" />

            <div className={estilos.cuerpo}>
              <p className={estilos.titulo}>
                {evento.documento_tipo !== tipo && evento.documento_numero && (
                  <span className={estilos.documento}>{evento.documento_numero}</span>
                )}
                {TITULO_EVENTO[evento.tipo]}
                {desde && hasta && (
                  <span className={estilos.transicion}>
                    {' '}
                    · {desde} → {hasta}
                  </span>
                )}
                {!desde && hasta && <span className={estilos.transicion}> · {hasta}</span>}
              </p>

              <p className={estilos.meta}>
                {evento.actor_nombre}
                {evento.actor_rol &&
                  ` (${
                    NOMBRE_ROL[evento.actor_rol as keyof typeof NOMBRE_ROL] ?? evento.actor_rol
                  })`}{' '}
                · {fechaHora(evento.ocurrido_en)}
              </p>

              {evento.notas && <p className={estilos.notas}>{evento.notas}</p>}
            </div>
          </article>
        )
      })}
    </div>
  )
}
